import { defineStore } from "pinia";

import { unwrapRef } from "@/utils/reactive";
import { computed, ref } from "vue";
import { IPricing, Pricing } from "@/app/domain";

import { useDictStore } from "@/stores/dictStore";

import { useSpacesStore } from "@/stores/new/spaces.store";
import { useDistanceStore } from "@/modules/shipping/store/distance.store";
import { useNewEstimateStore } from "@/stores/new/estimateStore";
import { InstallationStrategiesRegistry } from "@/app/services/installation.service";
import { InstallationDiscountCreationStrategy } from "@/app/domain/strategies/index";
import { InstallationItem } from "@/app/domain/entities/installation";
import { SteelSystemCodes } from "@/app/constants";

export const useInstallationStore = defineStore(
    "installation-store",
    () => {
        const dicts = useDictStore();
        const distanceStore = useDistanceStore();

        const estimateStore = useNewEstimateStore();
        const spacesStore = useSpacesStore();

        const estimate = computed(() => estimateStore.estimate);
        const spaces = computed(() => spacesStore.spaces);

        const systemCode = computed(() => estimate.value.installedSystemCode);
        const dealType = computed(() => estimateStore.estimate.dealTypeCode);

        const subtotalPrice = ref<IPricing>()
        const oldSubtotalPrice = ref<IPricing>()

        const totalSquare = ref(0);
        const discountIndex = ref(0);
        const discountSum = ref<IPricing>();

        const priceWithDiscount = ref<IPricing>();
        const overChargeSum = ref(0);
        const changedOverChargeSum = ref(0);
        const isOverchargeSelected = ref(false);

        const totalInstallationSum = ref<IPricing>();
        const halfInstallationSum = ref<IPricing>();

        const installationItemList = ref<InstallationItem[]>([]);
        const isInstallationPossible = ref(false);
        const isSteel = computed(() => SteelSystemCodes.includes(estimate.value.installedSystemCode));

        const $restoreInstallation = (estimate) => {
            const halfInstallationPrice = new Pricing(
                estimate.halfInstallation, estimate.halfInstallation, estimate.halfInstallation)
            const totalInstallationPrice = new Pricing(
                estimate.installationSum, estimate.installationSum, estimate.installationSum)

            overChargeSum.value = estimate.overChargeSum
            isInstallationPossible.value = estimate.installation
            halfInstallationSum.value = halfInstallationPrice
            totalInstallationSum.value = estimate?.isHalfInstallation ? halfInstallationPrice :
                totalInstallationPrice
        }

        const createInstallationItemList = () => {
            const registry = new InstallationStrategiesRegistry();
            spaces.value.forEach((space) => {
                space.getAllPartitions().forEach((partition) => {
                    const InstallationStrategy = registry.getStrategy(
                        partition.structureType ?? "",
                    );

                    if (!InstallationStrategy) return null;

                    const strategy = new InstallationStrategy(
                        partition,
                        isSteel.value,
                        estimate.value.installedSystemCode,
                        distanceStore.distance?.rounded_time || 4,
                        space?.title
                    );

                    const installationItem = strategy.create() as InstallationItem;

                    // Check is it a new item or update existing one
                    const existingIndex = installationItemList.value.findIndex(
                        (item) => unwrapRef(item.parentId) === installationItem.parentId.value
                    );

                    if (existingIndex === -1) {
                        // @ts-ignore
                        installationItemList.value.push(installationItem);
                        return;
                    }
                    // @ts-ignore
                    installationItemList.value[existingIndex] = installationItem;
                })
            })
        };

        const createInstallationDiscount = () => {
            const strategy = new InstallationDiscountCreationStrategy(unwrapRef(installationItemList.value))
            const installationDiscount = strategy.create()

            totalSquare.value = installationDiscount.qty.value;
            discountSum.value = installationDiscount.sum;
            discountIndex.value = installationDiscount.price.getSystemPrice(dealType.value).getValue();
        }

        const checkInstallationOverChargeNeeded = () => {
            if (!isInstallationPossible.value) {
                console.log("==Installation is unavailable because of zipcode==")
                estimate.value.installation = false
                return false;
            }

            const roundedTime = distanceStore.distance?.rounded_time || 4;

            const installationAvailabilityInfo = dicts.findDeliveryPriceSystemMin(systemCode.value, roundedTime)
            const currentPrice = priceWithDiscount.value?.getSystemPrice(dealType.value).getValue();

            if (
                !installationAvailabilityInfo?.price_min ||
                !installationAvailabilityInfo?.area ||
                !installationAvailabilityInfo?.time || !roundedTime
            ) {
                return
            }

            // reset overcharge
            overChargeSum.value = 0;

            if (roundedTime < installationAvailabilityInfo?.time) {
                console.log("==Installation time check passed==")
                return currentPrice;
            }

            if (currentPrice > installationAvailabilityInfo?.price_min) {
                console.log("==Installation price check passed==")
                return currentPrice;
            }

            const overCharge = installationAvailabilityInfo?.price_min - currentPrice;
            overChargeSum.value = overCharge;
            changedOverChargeSum.value = overCharge;
            console.log("==Installation need overcharge==")

            return overCharge + currentPrice;
        }


        const $reset = () => {
            subtotalPrice.value = new Pricing(0, 0, 0);
            oldSubtotalPrice.value = new Pricing(0, 0, 0);
            totalSquare.value = 0;
            discountIndex.value = 0;
            discountSum.value = new Pricing(0, 0, 0);
            priceWithDiscount.value = new Pricing(0, 0, 0);
            overChargeSum.value = 0;
            isOverchargeSelected.value = false;
            totalInstallationSum.value = new Pricing(0, 0, 0);
            halfInstallationSum.value = new Pricing(0, 0, 0);
            installationItemList.value = [];
            isInstallationPossible.value = false;
        };

        return {
            checkInstallationOverChargeNeeded,

            installationItemList,
            createInstallationItemList,
            createInstallationDiscount,
            totalSquare,
            subtotalPrice,
            oldSubtotalPrice,
            discountIndex,
            discountSum,
            priceWithDiscount,
            overChargeSum,
            isOverchargeSelected,
            totalInstallationSum,
            halfInstallationSum,
            changedOverChargeSum,
            isInstallationPossible,
            $reset,
            $restoreInstallation,
        };
    },
    { persist: true },
);
